type ResetFn = () => void;

const resets = new Map<string, ResetFn>();

/**
 * In-memory state that holds workspace-scoped content must register a reset
 * here, because `queryClient.clear()` does not reach it.
 *
 * This exists as a registry rather than `switchWorkspace` importing a dozen
 * zustand stores directly, for two reasons: the stores would become a circular
 * import away from the seam that resets them, and — more importantly — a
 * hard-coded list silently goes stale the moment someone adds the thirteenth
 * store. Registration puts the obligation next to the state that has it.
 *
 * Registering is idempotent by `key`, so a module that hot-reloads or mounts
 * twice does not accumulate duplicate resets.
 */
export function registerWorkspaceReset(key: string, reset: ResetFn): void {
  resets.set(key, reset);
}

export function unregisterWorkspaceReset(key: string): void {
  resets.delete(key);
}

/**
 * Runs every registered reset. One throwing reset must not prevent the others
 * from running: a half-cleared switch is how the previous workspace's content
 * survives into the new one, which is the failure this whole path prevents.
 */
export function runWorkspaceResets(): void {
  for (const [key, reset] of resets) {
    try {
      reset();
    } catch (err) {
      console.error(`[workspace] reset "${key}" failed`, err);
    }
  }
}

/** Test seam only. */
export function __clearWorkspaceResets(): void {
  resets.clear();
}
